import React, { ReactNode } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useWindowSize } from 'react-use';

import { LbPagination, Surface } from '@infoods/lifebase-ui';

import classes from './ChallengeLeaderboard.module.scss';
import { EmptyList } from '../../../shared/components/ui-components';
import { LbHorizontalScrollContainer } from '../../../shared/components/LbHorizontalScrollContainer/LbHorizontalScrollContainer';
import { leaderboardStore } from '../../stores/leaderboardStore';
import {
  columnsConfig,
  COLUMN_WIDTHS,
  ChallengeLeaderboardVariant,
  CHALLENGE_LEADERBOARD_VARIANT_CONFIG,
} from './ChallengeLeaderboard.settings';
import { CHALLENGE_STATUS } from '../../constants/challengeStatus';
import { SCORING_RULE } from '../../constants/scoringRule';
import { getIsRegistrationPeriod } from '../../utils/getIsRegistrationPeriod/getIsRegistrationPeriod';
import { TitleRowDesktop } from './components/TitleRow/TitleRowDesktop/TitleRowDesktop';
import { TitleRowMobile } from './components/TitleRow/TitleRowMobile/TitleRowMobile';
import { LeaderboardRowDesktop } from './components/LeaderboardRow/LeaderboardRowDesktop/LeaderboardRowDesktop';
import { LeaderboardRowMobile } from './components/LeaderboardRow/LeaderboardRowMobile/LeaderboardRowMobile';
import { EmptyListWrapper } from './components/EmptyListWrapper/EmptyListWrapper';
import { getGridTemplateColumns } from '../../../shared/components/table-components';

type ChallengeLeaderboardProps = {
  variant?: ChallengeLeaderboardVariant;
};

const OutBoxContainer = ({ children }: { children: ReactNode }) => {
  return <div>{children}</div>;
};

const InBoxContainer = ({ children }: { children: ReactNode }) => {
  return (
    <Surface radius="l" className={classes.inBoxContainer}>
      {children}
    </Surface>
  );
};

const ChallengeLeaderboard = observer(
  ({ variant = 'outBox' }: ChallengeLeaderboardProps) => {
    const { width } = useWindowSize();
    const isMobile = width < 768;

    const variantConfig = CHALLENGE_LEADERBOARD_VARIANT_CONFIG[variant];

    const scrollContainerClassName = classNames(classes.scrollContainer, {
      [classes.outBox]: variant === 'outBox',
    });
    const { challengeInfo, leaderboardList, filterMethods } = leaderboardStore;

    const {
      isShowPlaceholder,
      isSearchUsed,
      getPaginationProps,
      elementsPerPage,
      totalElements,
    } = filterMethods;

    if (!challengeInfo) {
      return null;
    }

    const Container = variant === 'inBox' ? InBoxContainer : OutBoxContainer;

    const leaderboardColumns =
      columnsConfig[challengeInfo.scoringRules || SCORING_RULE.FAT_LOSS];

    const desktopGridTemplateColumns = getGridTemplateColumns(
      leaderboardColumns,
      COLUMN_WIDTHS,
    );

    const isRegistrationPeriod =
      !!challengeInfo.importantDates &&
      getIsRegistrationPeriod(
        challengeInfo.importantDates.registrationStartDate,
        challengeInfo.importantDates.registrationEndDate,
      );

    const hasChallengeStarted =
      challengeInfo.status === CHALLENGE_STATUS.ACTIVE ||
      challengeInfo.status === CHALLENGE_STATUS.FINISHED;

    const emptyPlaceholderDescription =
      isRegistrationPeriod || hasChallengeStarted
        ? 'There are no participants'
        : `The challenge hasn't started`;

    return (
      <Container>
        <LbHorizontalScrollContainer
          variant={variantConfig.scrollContainer}
          scrollBarClassName={scrollContainerClassName}
        >
          <div
            className={classes.leaderboardTable}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? 'auto auto 1fr auto'
                : desktopGridTemplateColumns,
            }}
          >
            {isMobile ? (
              <TitleRowMobile variant={variantConfig.tableRow} />
            ) : (
              <TitleRowDesktop
                variant={variantConfig.tableRow}
                leaderboardColumns={leaderboardColumns}
              />
            )}
            {leaderboardList.map((item) =>
              isMobile ? (
                <LeaderboardRowMobile
                  variant={variantConfig.mobileRow}
                  key={item.participantId}
                  leaderboardRow={item}
                  leaderboardColumns={leaderboardColumns}
                />
              ) : (
                <LeaderboardRowDesktop
                  variant={variantConfig.tableRow}
                  key={item.participantId}
                  leaderboardRow={item}
                  leaderboardColumns={leaderboardColumns}
                />
              ),
            )}
          </div>
        </LbHorizontalScrollContainer>
        {isShowPlaceholder && (
          <EmptyListWrapper variant={variantConfig.emptyList}>
            <EmptyList
              show
              isSearch={isSearchUsed}
              description={emptyPlaceholderDescription}
            />
          </EmptyListWrapper>
        )}
        <LbPagination
          variant={variantConfig.pagination}
          {...getPaginationProps()}
          elementsPerPage={elementsPerPage}
          totalElements={totalElements}
        />
      </Container>
    );
  },
);

export { ChallengeLeaderboard };
